import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";

import { Select } from "../../../common/Select";
import { ConfirmationModal } from "../../../common/Modal";

/**
 * AddEditVesselFunctionModal
 *
 * Props:
 *   isOpen       — boolean
 *   onClose      — () => void
 *   mode         — "add" | "edit"
 *   selectedData — row object when editing, null when adding
 *   options      — { vesselFunctions: [] }
 *   onAdd        — (rowForm) => void
 *   onUpdate     — (rowForm, index) => void
 */
export const AddEditVesselFunctionModal = ({
  isOpen,
  onClose,
  mode,
  selectedData,
  options,
  onAdd,
  onUpdate,
}) => {
  const emptyForm = {
    vesselFunctionId: "",
    isActive: true,
  };

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);

  // ── Sync form when modal opens ──────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setErrors({});
      setForm(
        mode === "edit" && selectedData
          ? {
            vesselFunctionId: selectedData.vesselFunctionId || "",
            isActive: selectedData.isActive ?? true,
          }
          : emptyForm
      );
    }
  }, [isOpen, mode, selectedData]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // ── Validate ────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.vesselFunctionId) e.vesselFunctionId = "Vessel Function is required.";
    return e;
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setShowConfirmation(true);
  };

  const doConfirm = () => {
    if (mode === "add") {
      onAdd(form);
    } else {
      onUpdate(form, selectedData._index);
    }
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 !mt-0">
        <div className="bg-white w-full max-w-md rounded-lg shadow-2xl flex flex-col">

          {/* Header */}
          <div className="flex justify-between items-center px-5 py-3 border-b bg-gray-50 rounded-t-lg">
            <h3 className="text-sm font-bold text-blue-900">
              {mode === "add" ? "Add Vessel Function" : "Edit Vessel Function"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer"
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">

            {/* VesselFunction dropdown — disabled on edit since you're only toggling IsActive */}
            <Select
              label="Vessel Function:"
              options={options?.vesselFunctions || []}
              value={form.vesselFunctionId || ""}
              onChange={(e) => set("vesselFunctionId", e.target.value)}
              disabled={false} 
              isRequired={true}
              error={!!errors.vesselFunctionId}
              errorMessage={errors.vesselFunctionId}
            />
            {/* IsActive  */}
            <div className="flex items-center pt-2">
              <input
                type="checkbox"
                checked={form.isActive !== undefined ? form.isActive : true}
                onChange={(e) => set("isActive", e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-600">
                Active
              </label>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-400 text-white text-sm rounded cursor-pointer hover:bg-gray-500 border-none transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-blue-600 text-white text-sm font-bold rounded cursor-pointer hover:bg-blue-700 border-none transition-colors"
            >
              {mode === "add" ? "Add" : "Update"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={doConfirm}
        title={mode === "add" ? "Add Vessel Function" : "Update Vessel Function"}
        message={
          mode === "add"
            ? "Add this vessel function?"
            : "Update this vessel function?"
        }
      />
    </>
  );
};
